'use client';

import {
  useCallback,
  useEffect,
  createContext,
  useState,
  useContext,
} from 'react';
import { Questionnaire } from '@/_types/models/questionnaire';
import type { QuestionnaireIndexSerializer } from '@/_types/serializers/v1/questionnaires';
import { UserContext } from '../../UserProvider';
import { getAdminQuestionnaires } from '@/_api/admin-questionnaires';
import { OverviewContext } from '@/_contexts/admin/OverviewProvider';
import { getFirmQuestionnaireBacklog } from '@/_api/admin-firms';
import { SendQuestionnaireDialog } from './components/SendQuestionnaireDialog';
import { QuestionnairePreviewDialog } from './components/QuestionnairePreviewDialog';
import { ExportQuestionnairesDialog } from './components/ExportQuestionnairesDialog';
import { ToastContext } from '@/_contexts/ToastProvider';
import { getQuestionnaire } from '@/_api/client-questionnaires';
import type { QuestionnaireProviderProps } from './QuestionnaireProvider.types';
import type {
  OverviewFilterObject,
  OverviewColumnType,
} from '@/_types/overview';
import { NotificationsContext } from '../NotificationsProvider';
import { usePathname } from 'next/navigation';
import { Company } from '@/_types/models/company';
import { getCompanyById } from '@/_api/admin-companies';
// For temporary testing
// import { SAMPLE_QUESTIONNAIRES } from '@/__mocks__/questionnaire';

const QuestionnaireContext = createContext<QuestionnaireProviderProps>({
  highlightedQuestionIds: [],
  setHighlightedQuestionIds: () => null,
  isBUPReviewDialogOpenForSource: null,
  isBulkUploadProcessing: false,
  isLoading: true,
  isLoadingBacklogCount: false,
  isLoadingQuestionnairePreview: false,
  isQuestionnairePreviewOpen: false,
  isSending: false,
  isSendingTemplate: false,
  loadQuestionnaireBacklogCount: () => Promise.resolve(),
  loadQuestionnaires: (
    _incomingFilter?: OverviewFilterObject,
    _isSilent?: boolean,
  ) => Promise.resolve(),
  onExportToggle: () => null,
  questionnaireBacklogCount: 0,
  questionnaireCount: 0,
  questionnaireExport: [],
  questionnairePreview: undefined,
  questionnairePreviewCompany: undefined,
  questionnaires: [],
  reloadQuestionnairePreview: (
    _passedCompanyId?: number,
    _passedQuestionnaireId?: number,
    _isReloadOnly?: boolean,
  ) => Promise.resolve(undefined),
  selectedQuestionnaireCount: 0,
  selectedQuestionnaires: [],
  setIsBUPReviewDialogOpenForSource: () => null,
  setIsBulkUploadProcessing: () => null,
  setLoading: () => null,
  setQuestionnaireExport: () => null,
  setQuestionnairePreview: () => null,
  setQuestionnairePreviewOpen: () => null,
  setQuestionnaires: () => null,
  setSelectedQuestionnaires: () => null,
  setSending: (_newState: boolean, _preselectedEntityIds?: Array<number>) =>
    null,
  setSendingTemplate: () => null,
  setShouldAutoOpenBUPOnPreview: () => null,
  shouldAutoOpenBUPOnPreview: false,
  updateAQuestionnaire: (_questionnaire: Partial<Questionnaire>) => null,
});

const QuestionnaireProvider: any = ({ children }: any) => {
  // Sending
  const [isSending, setSending] = useState<boolean>(false);
  const [preselectedEntityIds, setPreselectedEntityIds] = useState<
    Array<number>
  >([]);
  const [isSendingTemplate, setSendingTemplate] = useState<boolean>(false);
  const [questionnairePreview, setQuestionnairePreview] = useState<
    Questionnaire | undefined
  >(undefined);
  const [questionnairePreviewCompany, setQuestionnairePreviewCompany] =
    useState<Company | undefined>(undefined);

  const [highlightedQuestionIds, setHighlightedQuestionIds] = useState<
    Array<number>
  >([]);

  const pathName: string = usePathname();

  // Exporting
  const [isExporting, setExporting] = useState<boolean>(false);
  const [questionnaireExport, setQuestionnaireExport] = useState<
    Array<Questionnaire>
  >([]);

  const { addToast } = useContext(ToastContext);
  const { isAdvisor, token } = useContext(UserContext);
  const { filter, sorting } = useContext(OverviewContext);
  const { reloadNotifications } = useContext(NotificationsContext);

  const [isQuestionnairePreviewOpen, setQuestionnairePreviewOpen] =
    useState<boolean>(false);
  const [isMounted, setMounted] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isLoadingQuestionnairePreview, setLoadingQuestionnairePreview] =
    useState<boolean>(false);
  const [questionnaireCount, setQuestionnaireCount] = useState<number>(0);
  const [questionnaires, setQuestionnaires] = useState<
    Array<QuestionnaireIndexSerializer>
  >([]);
  const [isLoadingBacklogCount, setIsLoadingBacklogCount] =
    useState<boolean>(true);
  const [questionnaireBacklogCount, setQuestionnaireBacklogCount] =
    useState<number>(0);
  const [isBulkUploadProcessing, setIsBulkUploadProcessing] =
    useState<boolean>(false);
  const [isBUPReviewDialogOpenForSource, setIsBUPReviewDialogOpenForSource] =
    useState<string | null | undefined>(null);
  const [shouldAutoOpenBUPOnPreview, setShouldAutoOpenBUPOnPreview] =
    useState<boolean>(false);

  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState<
    Array<QuestionnaireIndexSerializer>
  >([]);

  const selectedQuestionnaireCount = selectedQuestionnaires?.length ?? 0;

  const loadQuestionnaireBacklogCount = useCallback(async () => {
    if (!token || !isAdvisor) return;

    try {
      const { backlog_count, error } = await getFirmQuestionnaireBacklog({
        token,
      });

      if (!error) {
        setQuestionnaireBacklogCount(backlog_count ?? 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingBacklogCount(false);
    }
  }, [token, isAdvisor]);

  const loadQuestionnaires = useCallback(
    async (incomingFilter?: OverviewFilterObject, isSilent?: boolean) => {
      if (!token || !isAdvisor) return;

      try {
        if (!isSilent) setLoading(true);

        const latestFilter: OverviewFilterObject = {
          ...(incomingFilter || filter),
          sort_by: (sorting?.[0]?.id as OverviewColumnType) || 'last_activity',
          descending: !Boolean(sorting?.[0]?.desc),
        };

        // Load questionnaire count
        const { count } = (await getAdminQuestionnaires({
          ...latestFilter,
          count: true,
          token,
        })) as any;

        setQuestionnaireCount(count);

        // Load questionnaires
        const questionnaires = await getAdminQuestionnaires({
          ...latestFilter,
          token,
        });

        setQuestionnaires(questionnaires);
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setLoading(false);
      }
    },
    [token, sorting, filter, addToast, isAdvisor],
  );

  // Initial load
  useEffect(() => {
    setMounted(true);
  }, []);

  // Reload on any filter change
  useEffect(() => {
    if (isMounted) {
      loadQuestionnaires();
    }
  }, [filter, loadQuestionnaires]); // eslint-disable-line react-hooks/exhaustive-deps

  const onExportToggle = () => setExporting(!isExporting);

  // Reload the already selected for preview Questionnaire
  const reloadQuestionnairePreview = useCallback(
    async (
      passedCompanyId?: number,
      passedQuestionnaireId?: number,
      isOnlyReload?: boolean,
      isSilent?: boolean,
    ): Promise<Questionnaire | undefined> => {
      let response: Questionnaire | undefined;
      const localCompanyId: number =
        passedCompanyId ?? questionnairePreview?.company_id!;

      if (!token || typeof localCompanyId !== 'number') return;

      try {
        !isSilent && setLoadingQuestionnairePreview(true);

        response = await getQuestionnaire({
          companyId: localCompanyId!,
          questionnaireId: passedQuestionnaireId || questionnairePreview?.id!,
          token,
        });

        const company = await getCompanyById({
          token,
          companyId: localCompanyId,
        });

        if (!company?.error) {
          setQuestionnairePreviewCompany(company);
        }

        if (!isOnlyReload || !questionnairePreview?.id) {
          setQuestionnairePreview(response);
        }
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setLoadingQuestionnairePreview(false);
      }

      // Need to return it since its return is being used in places
      // like BulkUploadProcessor.
      return response;
    },
    [
      questionnairePreview?.company_id,
      questionnairePreview?.id,
      token,
      addToast,
    ],
  );

  // Whenever the incoming Partial<Questionnaire> gets updated, we should load the full context of it
  useEffect(() => {
    questionnairePreview?.id && reloadQuestionnairePreview();
  }, [questionnairePreview?.id, reloadQuestionnairePreview]);

  useEffect(() => {
    reloadNotifications(true);
  }, [pathName, reloadNotifications]);

  const onCloseExport = () => {
    setExporting(false);
    reloadNotifications(true, 6000);
  };

  const onClosePreview = (): void => {
    if (!isQuestionnairePreviewOpen) {
      return;
    }

    setQuestionnairePreview(undefined);
    setQuestionnairePreviewCompany(undefined);
    setQuestionnairePreviewOpen(false);
    setHighlightedQuestionIds([]);
  };

  const setSendingWithEntityIds = (
    isNewSending: boolean,
    entityIds?: Array<number>,
  ) => {
    setSending(isNewSending);
    setPreselectedEntityIds(entityIds || []);
  };

  const updateAQuestionnaire = (questionnaire: Partial<Questionnaire>) => {
    if (!questionnaire?.id) return;

    const existingQuestionnaireIndex = questionnaires.findIndex(
      (u) => u.id === questionnaire.id,
    );
    const newQuestionnaires = [...questionnaires];
    newQuestionnaires[existingQuestionnaireIndex] = {
      ...newQuestionnaires[existingQuestionnaireIndex],
      ...questionnaire,
    };

    setQuestionnaires(newQuestionnaires);
  };

  return (
    <QuestionnaireContext
      value={{
        highlightedQuestionIds,
        isBUPReviewDialogOpenForSource,
        isBulkUploadProcessing,
        isLoading,
        isLoadingBacklogCount,
        isLoadingQuestionnairePreview,
        isQuestionnairePreviewOpen,
        isSending,
        isSendingTemplate,
        loadQuestionnaireBacklogCount,
        loadQuestionnaires,
        onExportToggle,
        questionnaireBacklogCount,
        questionnaireCount,
        questionnaireExport,
        questionnairePreview,
        questionnairePreviewCompany,
        questionnaires,
        reloadQuestionnairePreview,
        selectedQuestionnaireCount,
        selectedQuestionnaires,
        setHighlightedQuestionIds,
        setIsBUPReviewDialogOpenForSource,
        setIsBulkUploadProcessing,
        setLoading,
        setQuestionnaireExport,
        setQuestionnairePreview,
        setQuestionnairePreviewOpen,
        setQuestionnaires,
        setSelectedQuestionnaires,
        setSending: setSendingWithEntityIds,
        setSendingTemplate,
        setShouldAutoOpenBUPOnPreview,
        shouldAutoOpenBUPOnPreview,
        updateAQuestionnaire,
      }}
    >
      <QuestionnairePreviewDialog
        isOpen={isQuestionnairePreviewOpen}
        onClose={onClosePreview}
      />
      <ExportQuestionnairesDialog
        isOpen={isExporting}
        onClose={onCloseExport}
        onClosePreview={onClosePreview}
        questionnaires={questionnaireExport}
      />
      <SendQuestionnaireDialog preselectedEntityIds={preselectedEntityIds} />
      {children}
    </QuestionnaireContext>
  );
};

export { QuestionnaireContext, QuestionnaireProvider };
